import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { setMailPassword } from '../utils/mailCredentials';
import RegisterWizard from './RegisterWizard';
import './Login.css';

/**
 * Login Component
 * Handles user login and registration
 * Features:
 * - Login form with email and password
 * - Registration form
 * - JWT token storage
 * - Error handling
 */
const Login = ({ onLogin, isAddingAccount = false, initialMode = 'login' }) => {
  const [isLogin, setIsLogin] = useState(initialMode !== 'register');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [showEmailSuggestion, setShowEmailSuggestion] = useState(false);
  const [emailSuggestion, setEmailSuggestion] = useState('');
  const [atozasEnabled, setAtozasEnabled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Map ATOZAS SSO callback error codes to friendly messages.
  const ATOZAS_ERRORS = {
    invalid_state: 'Your ATOZAS sign-in session expired. Please try again.',
    missing_code: 'ATOZAS sign-in was cancelled or failed. Please try again.',
    token_exchange_failed: 'Could not complete ATOZAS sign-in. Please try again.',
    invalid_token: 'ATOZAS returned an invalid token. Please try again.',
    userinfo_failed: 'Could not read your ATOZAS profile. Please try again.',
    missing_email: 'Your ATOZAS account has no email. An email is required.',
    missing_subject: 'Could not read your ATOZAS identity. Please try again.',
    provider_error: 'ATOZAS reported an error during sign-in.',
    authorize_failed: 'Could not start ATOZAS sign-in. Please try again.',
    callback_failed: 'ATOZAS sign-in failed. Please try again.',
  };

  // Discover whether ATOZAS SSO is enabled on the server.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get('/auth/atozas/config');
        if (cancelled) return;
        const enabled = Boolean(data && data.enabled);
        setAtozasEnabled(enabled);

        const params = new URLSearchParams(location.search);
        const err = params.get('atozas_error');
        let loggedOut = false;
        try {
          loggedOut =
            localStorage.getItem('atozas_logged_out') === '1' ||
            sessionStorage.getItem('atozas_logged_out') === '1';
        } catch (_e) {
          loggedOut = false;
        }
        // Auto-start SSO only when the user did not explicitly log out and did
        // not just fail a callback (avoids authorize ↔ login loops).
        if (enabled && data.autoRedirect && !err && !loggedOut) {
          const returnTo = params.get('returnTo');
          const safeReturn =
            returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')
              ? returnTo
              : '/inbox';

          // Prefer an existing IdP product-card SSO cookie over starting a new
          // authorize — otherwise finish→login soft-bounces re-enter OIDC and
          // loop with /inbox until a hard refresh.
          try {
            const deviceId = localStorage.getItem('deviceId') || undefined;
            const me = await axiosInstance.get('/auth/atozas/me', {
              params: deviceId ? { deviceId } : undefined,
            });
            if (cancelled) return;
            if (
              me.data &&
              me.data.authenticated &&
              me.data.token &&
              me.data.user &&
              !me.data.needsMailbox
            ) {
              localStorage.setItem('token', me.data.token);
              localStorage.setItem('user', JSON.stringify(me.data.user));
              if (me.data.mailPassword) {
                setMailPassword(me.data.mailPassword, me.data.user.email);
              }
              window.location.replace(
                me.data.returnTo && me.data.returnTo.startsWith('/')
                  ? me.data.returnTo
                  : safeReturn
              );
              return;
            }
          } catch (_e) {
            /* fall through to authorize redirect */
          }

          window.location.href = `/auth/atozas?returnTo=${encodeURIComponent(safeReturn)}`;
        }
      } catch (_e) {
        if (!cancelled) setAtozasEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Surface any ATOZAS callback error passed back via ?atozas_error=...
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const err = params.get('atozas_error');
    if (err) {
      setMessage({ type: 'error', text: ATOZAS_ERRORS[err] || 'ATOZAS sign-in failed. Please try again.' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleAtozasLogin = () => {
    // The user explicitly started SSO sign-in; allow its callback to establish
    // a fresh application session after a previous logout or expiry.
    localStorage.removeItem('atozas_logged_out');
    sessionStorage.removeItem('atozas_logged_out');
    const params = new URLSearchParams(location.search);
    const returnTo = params.get('returnTo');
    const safeReturn = returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/inbox';
    window.location.href = `/auth/atozas?returnTo=${encodeURIComponent(safeReturn)}`;
  };

  const getOrCreateDeviceId = () => {
    const key = 'deviceId';
    let deviceId = localStorage.getItem(key);
    if (!deviceId) {
      const randomPart = Math.random().toString(36).slice(2, 12);
      deviceId = `dev_${Date.now()}_${randomPart}`;
      localStorage.setItem(key, deviceId);
    }
    return deviceId;
  };

  // Pre-fill email from URL; sync login/register mode from route
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const email = params.get('email');
    if (email) {
      setFormData(prev => ({ ...prev, email }));
    }

    const modeFromQuery = params.get('mode');
    const isRegisterRoute = location.pathname === '/register';
    setIsLogin(!(isRegisterRoute || modeFromQuery === 'register'));
  }, [location]);

  /**
   * Handle email input change with autocomplete suggestion for login mode
   */
  const handleEmailChange = (e) => {
    const inputValue = e.target.value;
    const domain = '@atozasmail.in';

    if (inputValue.includes('@')) {
      const atIndex = inputValue.lastIndexOf('@');
      const beforeAt = inputValue.substring(0, atIndex + 1);
      const afterAt = inputValue.substring(atIndex + 1);

      if (inputValue.endsWith(domain)) {
        setShowEmailSuggestion(false);
        setEmailSuggestion('');
      } else if (afterAt.length === 0) {
        setShowEmailSuggestion(true);
        setEmailSuggestion(beforeAt + 'atozasmail.in');
      } else if (domain.startsWith('@' + afterAt)) {
        setShowEmailSuggestion(true);
        setEmailSuggestion(beforeAt + 'atozasmail.in');
      } else {
        setShowEmailSuggestion(false);
        setEmailSuggestion('');
      }
    } else {
      setShowEmailSuggestion(false);
      setEmailSuggestion('');
    }

    setFormData({
      ...formData,
      email: inputValue,
    });

    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  /**
   * Accept email suggestion
   */
  const acceptEmailSuggestion = () => {
    if (emailSuggestion) {
      setFormData({
        ...formData,
        email: emailSuggestion,
      });
      setShowEmailSuggestion(false);
      setEmailSuggestion('');
    }
  };

  /**
   * Handle email input keydown for suggestion acceptance
   */
  const handleEmailKeyDown = (e) => {
    if (isLogin && showEmailSuggestion) {
      if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault();
        acceptEmailSuggestion();
      } else if (e.key === 'Escape') {
        setShowEmailSuggestion(false);
        setEmailSuggestion('');
      }
    }
  };

  /**
   * Handle input change for other fields
   */
  const handleChange = (e) => {
    if (e.target.name === 'email') {
      handleEmailChange(e);
    } else {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
      });
      // Clear message when user types
      if (message.text) {
        setMessage({ type: '', text: '' });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const deviceId = getOrCreateDeviceId();
      const payload = { email: formData.email, password: formData.password, deviceId };

      const response = await axiosInstance.post('/api/auth/login', payload);

      if (response.data.success) {
        const { user, token } = response.data.data;

        // Store token and user in localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        // Same password is used for web login and Dovecot IMAP/SMTP
        setMailPassword(formData.password, formData.email);

        onLogin(user, isAddingAccount);
        navigate('/inbox', { replace: true });
      } else {
        setMessage({
          type: 'error',
          text: response.data.message || 'An error occurred',
        });
      }
    } catch (error) {
      console.error('Auth error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'An error occurred. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggle between login and register forms
   */
  const toggleMode = () => {
    const nextIsLogin = !isLogin;
    setIsLogin(nextIsLogin);
    setFormData({ username: '', email: '', password: '' });
    setMessage({ type: '', text: '' });
    navigate(nextIsLogin ? '/login' : '/register', { replace: true });
  };

  if (!isLogin) {
    return (
      <RegisterWizard
        onLogin={onLogin}
        isAddingAccount={isAddingAccount}
        onSwitchToLogin={() => {
          setIsLogin(true);
          setMessage({ type: '', text: '' });
          navigate('/login', { replace: true });
        }}
      />
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <button
            type="button"
            className="login-back-home"
            onClick={() => navigate('/')}
          >
            ← Back to home
          </button>
          <img className="login-logo" src="/atozasmail-logo.png" alt="ATOZASMail" />
          <h2>Login</h2>
          <p className="subtitle">Welcome back to ATOZASMail</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <div className="email-input-wrapper-login">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onKeyDown={handleEmailKeyDown}
                onBlur={() => setTimeout(() => setShowEmailSuggestion(false), 200)}
                placeholder="username@atozasmail.in"
                className="form-input"
                required
                disabled={loading}
              />
              {showEmailSuggestion && emailSuggestion && (
                <div className="email-suggestion-box">
                  <span className="email-suggestion-text">
                    Press <kbd>Tab</kbd> or <kbd>Enter</kbd> to use: <strong>{emailSuggestion}</strong>
                  </span>
                  <button
                    type="button"
                    className="email-suggestion-button"
                    onClick={acceptEmailSuggestion}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    Use
                  </button>
                </div>
              )}
            </div>
            <small className="form-hint">Format: username@atozasmail.in</small>
          </div>

          <div className="form-group">
            <div className="password-label-row">
              <label htmlFor="password">Password</label>
              <button
                type="button"
                className="forgot-password-link"
                onClick={() => navigate('/forgot-password')}
                disabled={loading}
              >
                Forgot password?
              </button>
            </div>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter password"
              className="form-input"
              required
              minLength={6}
              disabled={loading}
            />
          </div>

          {message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={loading}
          >
            {loading ? 'Please wait...' : 'Login'}
          </button>
        </form>

        {atozasEnabled && (
          <>
            <div className="atozas-divider">
              <span>or</span>
            </div>
            <button
              type="button"
              className="atozas-sso-button"
              onClick={handleAtozasLogin}
              disabled={loading}
            >
              <span className="atozas-sso-badge">A</span>
              Continue with ATOZAS
            </button>
            {/* <p className="atozas-sso-hint">
              Use your ATOZAS account to sign in. New here? You’ll pick your
              @atozasmail.in address next.
            </p> */}
          </>
        )}

        <div className="login-footer">
          <p>
            Don&apos;t have an account?{' '}
            <button
              type="button"
              onClick={toggleMode}
              className="toggle-button"
              disabled={loading}
            >
              Create account
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;


